import {
  shouldKeepSpectrumClassNames,
  useDOMRef,
  useStyleProps
} from '@react-spectrum/utils'
import clsx from 'clsx'
import { DOMRef, ValidationState } from '@react-types/shared'
import { filterDOMProps } from '@react-aria/utils'
import { useLocale } from '@react-aria/i18n'
import { ModalProvider, useModalProvider } from '@react-aria/overlays'
import {
  ProviderContext,
  ProviderProps,
  ColorScheme,
  Scale,
  Breakpoints,
  Theme
} from '@react-types/provider'
import React, { useContext, useEffect, useRef } from 'react'
import { useColorScheme } from '../../hooks/useColorScheme'
import { useScale } from '../../hooks/useScale'
import { ProviderWrapperContainer } from './styledComponents'

const Context = React.createContext<ProviderContext | null>(null)
Context.displayName = 'ProviderContext'

const DEFAULT_BREAKPOINTS = { S: 640, M: 768, L: 1024, XL: 1280, XXL: 1536 }

function Provider(props: ProviderProps, ref: DOMRef<HTMLDivElement>) {
  const prevContext = useProvider()
  const prevBreakpoints = prevContext && prevContext.breakpoints
  const { theme = prevContext && prevContext.theme, defaultColorScheme } = props
  const autoColorScheme = useColorScheme(theme, defaultColorScheme)
  const autoScale = useScale(theme)
  const { locale: prevLocale } = useLocale()

  const {
    colorScheme = autoColorScheme,
    scale = prevContext ? prevContext.scale : autoScale,
    locale = prevContext ? prevLocale : null,
    breakpoints = prevContext ? prevBreakpoints : DEFAULT_BREAKPOINTS,
    children,
    isQuiet,
    isEmphasized,
    isDisabled,
    isRequired,
    isReadOnly,
    validationState,
    ...otherProps
  } = props

  const currentProps = {
    theme,
    breakpoints,
    colorScheme,
    scale,
    isQuiet,
    isEmphasized,
    isDisabled,
    isRequired,
    isReadOnly,
    validationState
  }

  const filteredProps: Record<
    string,
    boolean | Breakpoints | ColorScheme | Scale | Theme | ValidationState | null
  > = {}
  Object.entries(currentProps).forEach(
    ([key, value]) => value !== undefined && (filteredProps[key] = value)
  )

  const context = Object.assign({}, prevContext, filteredProps)

  let contents = children
  const domProps = filterDOMProps(otherProps)
  const { styleProps } = useStyleProps(otherProps, undefined)
  if (
    !prevContext ||
    props.locale ||
    theme !== prevContext.theme ||
    colorScheme !== prevContext.colorScheme ||
    scale !== prevContext.scale ||
    Object.keys(domProps).length > 0 ||
    otherProps.UNSAFE_className ||
    Object.keys(styleProps.style as React.HTMLAttributes<HTMLElement>).length >
      0
  ) {
    contents = (
      <ProviderWrapper
        {...props}
        UNSAFE_style={{
          isolation: !prevContext ? 'isolate' : undefined,
          ...styleProps.style
        }}
        ref={ref}
      >
        {contents}
      </ProviderWrapper>
    )
  }

  return (
    <Context.Provider value={context}>
      <ModalProvider>{contents}</ModalProvider>
    </Context.Provider>
  )
}

const _Provider = React.forwardRef(Provider)
export { _Provider as Provider }

const ProviderWrapper = React.forwardRef(function ProviderWrapper(
  props: ProviderProps,
  ref: DOMRef<HTMLDivElement>
) {
  const { children, ...otherProps } = props
  const { locale, direction } = useLocale()
  const { theme, colorScheme, scale } = useProvider()
  const { modalProviderProps } = useModalProvider()
  const { styleProps } = useStyleProps(otherProps)
  const domRef = useDOMRef(ref)

  const themeKey = Object.keys(theme[colorScheme])[0]
  const scaleKey = Object.keys(theme[scale])[0]

  const className = clsx(
    styleProps.className,
    theme[colorScheme][themeKey],
    theme[scale][scaleKey],
    theme.global ? Object.values(theme.global) : null,
    {
      'react-spectrum-provider': shouldKeepSpectrumClassNames,
      spectrum: shouldKeepSpectrumClassNames,
      [themeKey]: shouldKeepSpectrumClassNames,
      [scaleKey]: shouldKeepSpectrumClassNames
    }
  )

  const style = {
    ...styleProps.style,
    colorScheme:
      props.colorScheme ??
      colorScheme ??
      Object.keys(theme)
        .filter((k) => k === 'light' || k === 'dark')
        .join(' ')
  }

  const hasWarned = useRef(false)
  useEffect(() => {
    if (direction && domRef.current) {
      const closestDir =
        domRef.current.parentElement &&
        domRef.current.parentElement.closest('[dir]')
      const dir = closestDir && closestDir.getAttribute('dir')
      if (dir && dir !== direction && !hasWarned.current) {
        console.warn(
          `Language directions cannot be nested. ${direction} inside ${dir}.`
        )
        hasWarned.current = true
      }
    }
  }, [direction, domRef, hasWarned])

  return (
    <ProviderWrapperContainer
      {...filterDOMProps(otherProps)}
      {...styleProps}
      {...modalProviderProps}
      className={className}
      style={style}
      lang={locale}
      dir={direction}
      ref={domRef}
    >
      {children}
    </ProviderWrapperContainer>
  )
})

export function useProvider(): any {
  return useContext(Context)
}

export function useProviderProps<T>(props: T): T {
  const context = useProvider()
  if (!context) {
    return props
  }
  return Object.assign(
    {},
    {
      isQuiet: context.isQuiet,
      isEmphasized: context.isEmphasized,
      isDisabled: context.isDisabled,
      isRequired: context.isRequired,
      isReadOnly: context.isReadOnly,
      validationState: context.validationState
    },
    props
  )
}
